/* CONTEXT:START
Wave: 1
Status: stub
Feature: Submissions Metrics - Mock data generator for Wave 1 UI testing
Dependencies: [Updated PenholderMetrics interface, SectionedSubmissions interface, Realistic test data]
Risk-Level: low
Performance-Target: <100ms
Last-Updated: 2025-08-06
CONTEXT:END */

import { PenholderMetrics, SectionedSubmissions, Submission } from '../types';

/**
 * Mock data service for Wave 1 UI testing
 * Wave 2: Replace entire service with real API calls to backend
 */
export class MockPenholderDataService {
  /**
   * Generate realistic mock metrics for Wave 1 UI testing
   * Wave 2: Replace with real API call to backend
   */
  static generateMockMetrics(userId: string, year: number, month: number): PenholderMetrics {
    // Realistic numbers for August 2025 based on typical monthly volumes
    const submitted = 45;           // Total submissions this month
    const addedToReport = 12;       // ~27% selection rate (realistic for quality curation)
    const notChosen = 25;           // ~56% not selected (realistic rejection rate)
    const pendingReview = submitted - addedToReport - notChosen; // 8 remaining (~18% pending)
    
    return {
      penholderUserId: userId,
      year,
      month,
      submitted,
      addedToReport,
      pendingReview,
      notChosen,
      // Deprecated fields for backward compatibility during Wave 1
      totalAssigned: submitted,
      totalReviewed: addedToReport + notChosen,
      totalEdited: 5, // Realistic number of submissions with currentVersion !== originalVersion
      averageReviewTime: 2.5 // Average 2.5 hours per review
    };
  }

  /**
   * Generate mock sectioned submissions for Wave 1 UI testing
   * Wave 2: Replace with real API call to backend
   * 
   * Note: totalEdited is calculated as submissions where currentVersion !== originalVersion
   */
  static generateMockSectionedSubmissions(userId: string): SectionedSubmissions {
    const baseSubmission = {
      userId: 'user-123',
      userName: 'Alex Johnson',  // Default user name
      type: 'highlight' as const,
      month: 8,
      year: 2025,
      createdAt: new Date(),
      updatedAt: new Date(),
      currentVersion: '1.0',
      originalVersion: '1.0',
      addedToReport: false,
      isIncludedInReport: false
    };

    return {
      pendingReview: [
        { 
          ...baseSubmission, 
          id: 'sub-pending-1', 
          userId: 'user-001',
          userName: 'Sarah Chen',
          content: 'Successfully led the Q3 product launch, coordinating across 5 teams and delivering 2 weeks ahead of schedule', 
          workflowState: 'under_review' as const,
          type: 'highlight' as const
        },
        { 
          ...baseSubmission, 
          id: 'sub-pending-2', 
          userId: 'user-002',
          userName: 'Marcus Rodriguez',
          content: 'Implemented new automated testing framework that reduced deployment time by 60% and caught 15 critical bugs', 
          workflowState: 'under_review' as const,
          type: 'highlight' as const,
          // This submission was edited (multiple versions)
          currentVersion: '1.1',
          originalVersion: '1.0',
          penholderEditedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString()
        },
        { 
          ...baseSubmission, 
          id: 'sub-pending-3', 
          userId: 'user-003',
          userName: 'Emily Watson',
          content: 'Mentored 3 junior developers, helping them achieve promotion readiness through structured learning plans', 
          workflowState: 'under_review' as const,
          type: 'highlight' as const
        },
        { 
          ...baseSubmission, 
          id: 'sub-pending-4', 
          userId: 'user-004',
          userName: 'David Kim',
          content: 'Resolved critical production outage in 45 minutes, preventing $50K in potential revenue loss', 
          workflowState: 'under_review' as const,
          type: 'highlight' as const
        },
        { 
          ...baseSubmission, 
          id: 'sub-pending-5', 
          userId: 'user-005',
          userName: 'Jessica Taylor',
          content: 'Database migration took 8 hours instead of planned 4 hours due to unexpected schema conflicts', 
          workflowState: 'under_review' as const,
          type: 'lowlight' as const
        },
        { 
          ...baseSubmission, 
          id: 'sub-pending-6', 
          userId: 'user-006',
          userName: 'Michael Brown',
          content: 'Client presentation was delayed by 30 minutes due to technical difficulties with demo environment', 
          workflowState: 'under_review' as const,
          type: 'lowlight' as const
        },
        { 
          ...baseSubmission, 
          id: 'sub-pending-7', 
          content: 'Optimized API response times by 40% through database query improvements and caching strategy', 
          workflowState: 'under_review' as const,
          type: 'highlight' as const
        },
        { 
          ...baseSubmission, 
          id: 'sub-pending-8', 
          content: 'Security vulnerability discovered in authentication module required emergency patch deployment', 
          workflowState: 'under_review' as const,
          type: 'lowlight' as const
        }
      ],
      addedToReport: [
        { 
          ...baseSubmission, 
          id: 'sub-added-1', 
          userId: 'user-007',
          userName: 'Lisa Anderson',
          content: 'Architected and delivered new microservices platform that improved system scalability by 300%', 
          workflowState: 'added_to_report' as const, 
          addedToReport: true,
          type: 'highlight' as const,
          addedToReportAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          addedToReportBy: userId
        },
        { 
          ...baseSubmission, 
          id: 'sub-added-2', 
          userId: 'user-008',
          userName: 'Robert Wilson',
          content: 'Led cross-functional initiative that reduced customer support tickets by 45% through proactive monitoring', 
          workflowState: 'added_to_report' as const, 
          addedToReport: true,
          type: 'highlight' as const,
          addedToReportAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
          addedToReportBy: userId,
          // This submission was edited (multiple versions)
          currentVersion: '1.2',
          originalVersion: '1.0',
          penholderEditedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString()
        },
        { 
          ...baseSubmission, 
          id: 'sub-added-3', 
          content: 'Established new code review process that improved code quality metrics by 35% and reduced bugs by 50%', 
          workflowState: 'added_to_report' as const, 
          addedToReport: true,
          type: 'highlight' as const,
          addedToReportAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          addedToReportBy: userId,
          // This submission was edited (multiple versions)
          currentVersion: '1.1',
          originalVersion: '1.0',
          penholderEditedAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString()
        },
        { 
          ...baseSubmission, 
          id: 'sub-added-4', 
          content: 'Delivered critical client feature 1 week early, resulting in contract extension worth $200K annually', 
          workflowState: 'added_to_report' as const, 
          addedToReport: true,
          type: 'highlight' as const,
          addedToReportAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
          addedToReportBy: userId
        },
        { 
          ...baseSubmission, 
          id: 'sub-added-5', 
          content: 'Major service outage lasted 3 hours due to insufficient monitoring of third-party dependencies', 
          workflowState: 'added_to_report' as const, 
          addedToReport: true,
          type: 'lowlight' as const,
          addedToReportAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
          addedToReportBy: userId,
          reportInclusionReason: 'Important learning opportunity for team resilience planning',
          // This submission was edited (multiple versions)
          currentVersion: '1.3',
          originalVersion: '1.0',
          penholderEditedAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString()
        },
        { 
          ...baseSubmission, 
          id: 'sub-added-6', 
          content: 'Implemented AI-powered recommendation engine that increased user engagement by 65%', 
          workflowState: 'added_to_report' as const, 
          addedToReport: true,
          type: 'highlight' as const,
          addedToReportAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
          addedToReportBy: userId
        },
        { 
          ...baseSubmission, 
          id: 'sub-added-7', 
          content: 'Spearheaded security audit that identified and resolved 12 critical vulnerabilities', 
          workflowState: 'added_to_report' as const, 
          addedToReport: true,
          type: 'highlight' as const,
          addedToReportAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
          addedToReportBy: userId
        },
        { 
          ...baseSubmission, 
          id: 'sub-added-8', 
          content: 'Created comprehensive documentation portal that reduced onboarding time for new developers by 50%', 
          workflowState: 'added_to_report' as const, 
          addedToReport: true,
          type: 'highlight' as const,
          addedToReportAt: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
          addedToReportBy: userId
        },
        { 
          ...baseSubmission, 
          id: 'sub-added-9', 
          content: 'Budget overrun of 25% on infrastructure costs due to inadequate capacity planning', 
          workflowState: 'added_to_report' as const, 
          addedToReport: true,
          type: 'lowlight' as const,
          addedToReportAt: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString(),
          addedToReportBy: userId,
          reportInclusionReason: 'Critical for improving future project estimation processes'
        },
        { 
          ...baseSubmission, 
          id: 'sub-added-10', 
          content: 'Launched mobile app feature that achieved 90% user adoption rate within first month', 
          workflowState: 'added_to_report' as const, 
          addedToReport: true,
          type: 'highlight' as const,
          addedToReportAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
          addedToReportBy: userId
        },
        { 
          ...baseSubmission, 
          id: 'sub-added-11', 
          content: 'Established partnership with key vendor that reduced operational costs by $75K annually', 
          workflowState: 'added_to_report' as const, 
          addedToReport: true,
          type: 'highlight' as const,
          addedToReportAt: new Date(Date.now() - 11 * 24 * 60 * 60 * 1000).toISOString(),
          addedToReportBy: userId
        },
        { 
          ...baseSubmission, 
          id: 'sub-added-12', 
          content: 'Designed and implemented disaster recovery plan that passed all compliance audits', 
          workflowState: 'added_to_report' as const, 
          addedToReport: true,
          type: 'highlight' as const,
          addedToReportAt: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(),
          addedToReportBy: userId
        }
      ],
      notChosen: [
        { 
          ...baseSubmission, 
          id: 'sub-notchosen-1', 
          userId: 'user-009',
          userName: 'Jennifer Lee',
          content: 'Attended weekly team standup meetings and provided status updates', 
          workflowState: 'not_added_to_report' as const,
          type: 'highlight' as const,
          reportExclusionReason: 'Routine activity, not significant enough for monthly report'
        },
        { 
          ...baseSubmission, 
          id: 'sub-notchosen-2', 
          userId: 'user-010',
          userName: 'Thomas Garcia',
          content: 'Updated project documentation with latest API changes', 
          workflowState: 'not_added_to_report' as const,
          type: 'highlight' as const,
          reportExclusionReason: 'Standard maintenance task'
        },
        { 
          ...baseSubmission, 
          id: 'sub-notchosen-3', 
          content: 'Fixed minor UI alignment issue in user profile page', 
          workflowState: 'not_added_to_report' as const,
          type: 'highlight' as const,
          reportExclusionReason: 'Minor bug fix, not report-worthy',
          // This submission was edited (multiple versions)
          currentVersion: '1.1',
          originalVersion: '1.0',
          penholderEditedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString()
        },
        { 
          ...baseSubmission, 
          id: 'sub-notchosen-4', 
          content: 'Participated in code review for colleague\'s pull request', 
          workflowState: 'not_added_to_report' as const,
          type: 'highlight' as const,
          reportExclusionReason: 'Routine collaboration activity'
        },
        { 
          ...baseSubmission, 
          id: 'sub-notchosen-5', 
          content: 'Completed mandatory security training certification', 
          workflowState: 'not_added_to_report' as const,
          type: 'highlight' as const,
          reportExclusionReason: 'Required training, not an achievement'
        }
        // Note: Only showing first 5 of 25 items for brevity in mock data
        // Real implementation would have all 25 items
      ]
    };
  }

  /**
   * Simulate API delay for realistic testing
   * Wave 2: Remove when connecting to real API
   */
  static async delay(ms: number = 500): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Mock API call for metrics with realistic delay
   * Wave 2: Replace with real penholderApiService.getMetrics()
   */
  static async getMockMetrics(userId: string, year: number, month: number): Promise<PenholderMetrics> {
    await this.delay(300); // Simulate API call delay
    return this.generateMockMetrics(userId, year, month);
  }

  /**
   * Mock API call for sectioned submissions with realistic delay
   * Wave 2: Replace with real penholderApiService.getAssignedSubmissions()
   */
  static async getMockSectionedSubmissions(userId: string): Promise<SectionedSubmissions> {
    await this.delay(400); // Simulate API call delay
    return this.generateMockSectionedSubmissions(userId);
  }
}
